import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent } from './ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import BiometricAuth from './BiometricAuth';

// Componente para cambiar contraseña
const PasswordChangeSection = ({ API }) => {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Las contraseñas nuevas no coinciden');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      toast.error('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setIsChangingPassword(true);

    try {
      const response = await axios.post(`${API}/auth/change-password-general`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });

      if (response.data.success) {
        toast.success('Contraseña cambiada exitosamente');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-start">
          <i className="fas fa-info-circle text-blue-500 mt-1 mr-3"></i>
          <div>
            <h4 className="font-semibold text-blue-800 mb-1">Cambiar Contraseña</h4>
            <p className="text-sm text-blue-600">
              Por tu seguridad, te recomendamos cambiar tu contraseña periódicamente.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handlePasswordChange} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="currentPassword">Contraseña Actual *</Label>
          <div className="relative">
            <Input
              id="currentPassword"
              type={showPasswords.current ? "text" : "password"}
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData(prev => ({...prev, currentPassword: e.target.value}))}
              required
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('current')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <i className={`fas ${showPasswords.current ? 'fa-eye-slash' : 'fa-eye'} text-gray-400`}></i>
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="newPassword">Nueva Contraseña *</Label>
          <div className="relative">
            <Input
              id="newPassword"
              type={showPasswords.new ? "text" : "password"}
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData(prev => ({...prev, newPassword: e.target.value}))}
              required
              minLength={6}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('new')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <i className={`fas ${showPasswords.new ? 'fa-eye-slash' : 'fa-eye'} text-gray-400`}></i>
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirmar Nueva Contraseña *</Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showPasswords.confirm ? "text" : "password"}
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData(prev => ({...prev, confirmPassword: e.target.value}))}
              required
              minLength={6}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('confirm')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <i className={`fas ${showPasswords.confirm ? 'fa-eye-slash' : 'fa-eye'} text-gray-400`}></i>
            </button>
          </div>
        </div>

        {passwordData.newPassword && passwordData.confirmPassword && 
         passwordData.newPassword !== passwordData.confirmPassword && (
          <p className="text-sm text-red-600">Las contraseñas no coinciden</p>
        )}

        <div className="pt-4">
          <Button
            type="submit"
            disabled={isChangingPassword || !passwordData.currentPassword || 
                     !passwordData.newPassword || !passwordData.confirmPassword ||
                     passwordData.newPassword !== passwordData.confirmPassword}
            className="w-full bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white"
          >
            <i className={`fas ${isChangingPassword ? 'fa-spinner fa-spin' : 'fa-lock'} mr-2`}></i>
            {isChangingPassword ? 'Cambiando Contraseña...' : 'Cambiar Contraseña'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Componente para sincronizar notificaciones push
const PushNotificationSection = ({ API, user }) => {
  const [status, setStatus] = useState('checking'); // checking, enabled, disabled, error
  const [syncing, setSyncing] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [serverStatus, setServerStatus] = useState(null);
  const [testingPush, setTestingPush] = useState(false);
  const [testPushResult, setTestPushResult] = useState(null);

  useEffect(() => {
    checkPushStatus();
    loadServerStatus();
  }, []);

  const loadServerStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/usuarios/mi-onesignal-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setServerStatus(await response.json());
      }
    } catch (e) {
      console.error('Error loading server status:', e);
    }
  };

  const handleTestPush = async () => {
    setTestingPush(true);
    setTestPushResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/usuarios/test-push-to-self`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTestPushResult(data);
      if (data.success) {
        toast.success('Notificación de prueba enviada. Deberías recibirla en unos segundos.');
      } else {
        toast.error('No se pudo enviar: ' + (data.error || 'Intenta re-sincronizar primero.'));
      }
    } catch (e) {
      toast.error('Error al enviar prueba');
    } finally {
      setTestingPush(false);
    }
  };

  const checkPushStatus = async () => {
    try {
      // Verificar si el navegador soporta notificaciones
      if (!('Notification' in window)) {
        setStatus('unsupported');
        return;
      }

      const permission = Notification.permission;
      console.log('📱 Browser permission:', permission);
      
      if (permission === 'denied') {
        setStatus('blocked');
        return;
      }

      if (permission === 'default') {
        setStatus('not_asked');
        return;
      }

      // Permission granted - verificar OneSignal
      if (window.OneSignal) {
        try {
          const isOptedIn = await window.OneSignal.User.PushSubscription.optedIn;
          const subId = window.OneSignal.User?.PushSubscription?.id;
          
          console.log('📱 OneSignal status:', { isOptedIn, subId: subId ? subId.substring(0, 20) + '...' : 'none' });
          
          if (isOptedIn) {
            // Usuario está suscrito en OneSignal
            setStatus('enabled');
            if (subId) {
              setSubscriptionId(subId);
            }
          } else if (subId) {
            // Tiene ID pero no optedIn - raro pero posible
            setStatus('enabled');
            setSubscriptionId(subId);
          } else {
            // No está suscrito
            setStatus('not_subscribed');
          }
        } catch (e) {
          console.error('Error checking OneSignal:', e);
          // Si hay permiso del navegador, probablemente está funcionando
          if (permission === 'granted') {
            setStatus('not_subscribed');
          } else {
            setStatus('error');
          }
        }
      } else {
        // OneSignal no cargado, esperar un poco
        console.log('📱 OneSignal not loaded yet, waiting...');
        setTimeout(checkPushStatus, 1000);
      }
    } catch (e) {
      console.error('Error checking push status:', e);
      setStatus('error');
    }
  };

  const handleSyncPush = async () => {
    // ⚠️ Bloquear en sesión de soporte (clave maestra)
    if (localStorage.getItem('is_support_session') === 'true') {
      toast.error('No puedes re-sincronizar OneSignal en una sesión de soporte. Volvé a tu cuenta de admin.');
      return;
    }
    setSyncing(true);
    try {
      // 1. Solicitar permiso si no lo tiene
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          toast.error('Debes permitir las notificaciones');
          setSyncing(false);
          return;
        }
      }

      // 2. RESET COMPLETO de OneSignal
      if (window.OneSignal) {
        console.log('📱 RESET COMPLETO de OneSignal...');
        
        // Paso A: Logout para romper asociación vieja con external_id
        try {
          await window.OneSignal.logout();
          console.log('📱 Logout completado - asociación vieja eliminada');
          await new Promise(resolve => setTimeout(resolve, 1500));
        } catch (e) {
          console.log('⚠️ Logout error (ignorable):', e.message);
        }
        
        // Paso B: Opt-out para invalidar suscripción local
        try {
          await window.OneSignal.User.PushSubscription.optOut();
          console.log('📱 OptOut completado');
          await new Promise(resolve => setTimeout(resolve, 1500));
        } catch (e) {
          console.log('⚠️ OptOut error (ignorable):', e.message);
        }
        
        // Paso C: Opt-in para generar suscripción NUEVA
        await window.OneSignal.User.PushSubscription.optIn();
        console.log('📱 OptIn completado');
        
        // Paso D: Esperar nuevo subscription ID
        let subId = null;
        for (let i = 0; i < 20; i++) {
          await new Promise(resolve => setTimeout(resolve, 500));
          subId = window.OneSignal.User?.PushSubscription?.id;
          console.log(`📱 Intento ${i+1}/20 - ID: ${subId ? subId.substring(0, 20) + '...' : 'pendiente'}`);
          if (subId) break;
        }

        if (!subId) {
          toast.error('No se pudo obtener el ID de suscripción. Cierra y abre la app e intenta de nuevo.');
          setSyncing(false);
          return;
        }

        setSubscriptionId(subId);

        // Paso E: Login con user ID para crear nueva asociación en OneSignal
        if (user?.id) {
          try {
            await window.OneSignal.login(user.id);
            console.log('✅ OneSignal login con nuevo perfil:', user.id);
            await new Promise(resolve => setTimeout(resolve, 1000));
          } catch (e) {
            console.log('⚠️ Login error:', e.message);
          }
        }

        // Paso F: Registrar email como contacto
        if (user?.email) {
          try {
            await window.OneSignal.User.addEmail(user.email);
            console.log('✅ Email registrado en OneSignal:', user.email);
          } catch (e) {
            console.log('⚠️ addEmail error:', e.message);
          }
        }
        
        // Paso G: Agregar tags con toda la info del usuario
        if (user) {
          await window.OneSignal.User.addTags({
            email: user.email,
            user_id: user.id,
            rol: user.rol || '',
            nombre: user.nombre || '',
            apellido: user.apellido || '',
            nombre_completo: `${user.nombre || ''} ${user.apellido || ''}`.trim()
          }).catch(() => {});
        }

        // 6. Guardar en el backend
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/usuarios/update-onesignal-subscription`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ subscription_id: subId })
        });

        console.log('📱 Response status:', response.status);
        
        if (response.ok) {
          const data = await response.json().catch(() => ({}));
          console.log('📱 Response data:', data);
          toast.success('¡Notificaciones re-sincronizadas correctamente!');
          setStatus('enabled');
          setSubscriptionId(subId);
          // Limpiar cache de sincronización
          localStorage.setItem('onesignal_synced', Date.now().toString());
        } else {
          const errorText = await response.text().catch(() => 'Error desconocido');
          console.error('📱 Error response:', errorText);
          if (subId) {
            toast.success('¡Notificaciones activadas! (sincronización parcial)');
            setStatus('enabled');
            setSubscriptionId(subId);
          } else {
            toast.error('Error al guardar en el servidor');
          }
        }
      } else {
        toast.error('OneSignal no está cargado. Recarga la página e intenta de nuevo.');
      }
    } catch (e) {
      console.error('Error syncing push:', e);
      toast.error('Error al sincronizar: ' + (e.message || 'Intenta de nuevo'));
    } finally {
      setSyncing(false);
    }
  };

  const getStatusInfo = () => {
    switch (status) {
      case 'checking':
        return { icon: 'fa-spinner fa-spin', color: 'text-gray-500', text: 'Verificando...' };
      case 'enabled':
        return { icon: 'fa-check-circle', color: 'text-green-500', text: 'Notificaciones activas' };
      case 'blocked':
        return { icon: 'fa-ban', color: 'text-red-500', text: 'Bloqueadas en el navegador' };
      case 'not_asked':
        return { icon: 'fa-question-circle', color: 'text-yellow-500', text: 'No configuradas' };
      case 'not_subscribed':
        return { icon: 'fa-exclamation-circle', color: 'text-orange-500', text: 'No sincronizadas' };
      case 'unsupported':
        return { icon: 'fa-times-circle', color: 'text-gray-500', text: 'No soportadas en este navegador' };
      default:
        return { icon: 'fa-exclamation-triangle', color: 'text-yellow-500', text: 'Estado desconocido' };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="space-y-4" data-testid="notification-diagnostic-card">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
        <div className="flex items-start">
          <i className="fas fa-bell text-purple-500 mt-1 mr-3"></i>
          <div>
            <h4 className="font-semibold text-purple-800 mb-1">Notificaciones Push</h4>
            <p className="text-sm text-purple-600">
              Recibe alertas importantes directamente en tu dispositivo.
            </p>
          </div>
        </div>
      </div>

      {/* Estado actual */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-3">
          <i className={`fas ${statusInfo.icon} ${statusInfo.color} text-xl`}></i>
          <div>
            <p className="font-medium text-gray-800">{statusInfo.text}</p>
            {subscriptionId && status === 'enabled' && (
              <p className="text-xs text-gray-500 truncate max-w-[200px]">
                ID: {subscriptionId.substring(0, 20)}...
              </p>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          {(status === 'not_asked' || status === 'not_subscribed' || status === 'error' || status === 'enabled') && (
            <Button
              onClick={handleSyncPush}
              disabled={syncing}
              className={status === 'enabled' ? "border-purple-300 text-purple-700" : "bg-purple-600 hover:bg-purple-700 text-white"}
              variant={status === 'enabled' ? "outline" : "default"}
              data-testid="force-resubscribe-btn"
            >
              {syncing ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  {status === 'enabled' ? '...' : 'Sincronizando...'}
                </>
              ) : (
                <>
                  <i className={`fas ${status === 'enabled' ? 'fa-redo' : 'fa-sync-alt'} mr-2`}></i>
                  {status === 'enabled' ? 'Re-sincronizar' : 'Activar'}
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Estado del servidor */}
      {serverStatus && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="p-3 bg-gray-50 rounded-lg" data-testid="server-notif-status">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 font-semibold">Estado en Servidor</p>
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2">
                <i className={`fas fa-circle text-xs ${serverStatus.subscription_id_in_users ? 'text-green-500' : 'text-red-500'}`}></i>
                <span>Suscripción: {serverStatus.subscription_id_in_users ? 'Registrada' : 'No registrada'}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className={`fas fa-circle text-xs ${serverStatus.ids_match ? 'text-green-500' : 'text-yellow-500'}`}></i>
                <span>Sincronización: {serverStatus.ids_match ? 'OK' : 'Desincronizado'}</span>
              </div>
            </div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg" data-testid="local-notif-status">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 font-semibold">Estado Local</p>
            <div className="space-y-1 text-sm">
              <div className="flex items-center gap-2">
                <i className={`fas fa-circle text-xs ${status === 'enabled' ? 'text-green-500' : 'text-red-500'}`}></i>
                <span>Navegador: {status === 'enabled' ? 'Suscrito' : status === 'blocked' ? 'Bloqueado' : 'No suscrito'}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className={`fas fa-circle text-xs ${subscriptionId ? 'text-green-500' : 'text-gray-400'}`}></i>
                <span>ID Local: {subscriptionId ? subscriptionId.substring(0, 25) + '...' : 'No disponible'}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botón de prueba */}
      <div className="flex flex-col gap-2 pt-2 border-t border-gray-200">
        <Button
          onClick={handleTestPush}
          disabled={testingPush}
          variant="outline"
          className="border-amber-300 text-amber-700 hover:bg-amber-50 w-full sm:w-auto"
          data-testid="test-push-btn"
        >
          {testingPush ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Enviando...
            </>
          ) : (
            <>
              <i className="fas fa-paper-plane mr-2"></i>
              Enviar Notificación de Prueba a Mí Mismo
            </>
          )}
        </Button>
        
        {testPushResult && (
          <div className={`p-3 rounded-lg text-sm ${testPushResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`} data-testid="test-push-result">
            <p className="font-medium mb-1">
              {testPushResult.success ? 'Notificación enviada' : 'Error al enviar'}
            </p>
            {testPushResult.message && <p className="text-xs">{testPushResult.message}</p>}
            {testPushResult.methods && Object.entries(testPushResult.methods).map(([method, result]) => (
              <div key={method} className="flex items-center gap-2 text-xs mt-1">
                <i className={`fas fa-circle ${result.success ? 'text-green-500' : 'text-red-400'}`}></i>
                <span>{method}: {result.success ? `OK (${result.recipients} destinatarios)` : (result.error || 'falló')}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {status === 'blocked' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">
            <i className="fas fa-info-circle mr-2"></i>
            Las notificaciones están bloqueadas. Para activarlas:
          </p>
          <ol className="text-sm text-red-600 mt-2 ml-6 list-decimal">
            <li>Haz clic en el ícono de candado en la barra de direcciones</li>
            <li>Busca "Notificaciones" y cámbialo a "Permitir"</li>
            <li>Recarga la página</li>
          </ol>
        </div>
      )}
    </div>
  );
};

const PerfilCompleto = () => {
  const { user, API, getImageUrl } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [perfilData, setPerfilData] = useState(null);
  const [historialAcademico, setHistorialAcademico] = useState(null);
  const [showAgregarDocumento, setShowAgregarDocumento] = useState(false);
  const [showAgregarCapacitacion, setShowAgregarCapacitacion] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  
  // Estados para foto de perfil
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Estados para formularios
  const [nuevoDocumento, setNuevoDocumento] = useState({
    tipo: 'certificado',
    nombre: '',
    url: '',
    fecha_emision: '',
    descripcion: ''
  });

  const [nuevaCapacitacion, setNuevaCapacitacion] = useState({
    tipo: 'externa',
    nombre: '',
    institucion: '',
    fecha_inicio: '',
    fecha_finalizacion: '',
    estado: 'completada',
    horas: '',
    certificado_url: '',
    notas: ''
  });

  // Estado para editar capacitación
  const [editandoCapacitacion, setEditandoCapacitacion] = useState(null);
  const [showEditarCapacitacion, setShowEditarCapacitacion] = useState(false);

  // Estados para editar información personal
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    nombre: '',
    apellido: '',
    celular: '',
    telefono: '',
    pais: '',
    ciudad: '',
    fecha_nacimiento: ''
  });

  useEffect(() => {
    // Inicializar datos del formulario cuando el usuario carga
    if (user) {
      setProfileFormData({
        nombre: user.nombre || '',
        apellido: user.apellido || '',
        celular: user.celular || '',
        telefono: user.telefono || '',
        pais: user.pais || '',
        ciudad: user.ciudad || '',
        fecha_nacimiento: user.fecha_nacimiento || ''
      });
    }
  }, [user]);

  useEffect(() => {
    cargarPerfilCompleto();
    if (['estudiante', 'pastor', 'graduado', 'profesor'].includes(user?.rol)) {
      cargarHistorialAcademico();
    }
  }, [user]);

  const cargarPerfilCompleto = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/${user.id}/perfil-completo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerfilData(response.data);
    } catch (error) {
      console.error('Error cargando perfil:', error);
      toast.error('Error al cargar perfil');
    } finally {
      setLoading(false);
    }
  };

  const cargarHistorialAcademico = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/me/historial-academico-completo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistorialAcademico(response.data);
    } catch (error) {
      console.error('Error cargando historial académico:', error);
    }
  };

  // Función para actualizar información personal
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        nombre: profileFormData.nombre || null,
        apellido: profileFormData.apellido || null,
        celular: profileFormData.celular || null,
        telefono: profileFormData.telefono || null,
        pais: profileFormData.pais || null,
        ciudad: profileFormData.ciudad || null,
        fecha_nacimiento: profileFormData.fecha_nacimiento || null
      };

      // Filtrar campos null para enviar solo los que tienen valor
      const filteredData = Object.fromEntries(
        Object.entries(updateData).filter(([_, v]) => v !== null && v !== '')
      );
      
      // Pero también incluir pais y ciudad explícitamente si tienen valor
      if (profileFormData.pais && profileFormData.pais.trim()) {
        filteredData.pais = profileFormData.pais.trim();
      }
      if (profileFormData.ciudad && profileFormData.ciudad.trim()) {
        filteredData.ciudad = profileFormData.ciudad.trim();
      }

      console.log('Enviando datos de perfil:', filteredData);

      await axios.put(`${API}/users/profile`, filteredData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Perfil actualizado exitosamente');
      
      // Recargar la página para actualizar el contexto del usuario
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error actualizando perfil:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleProfileInputChange = (field, value) => {
    setProfileFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAgregarDocumento = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/me/documentos`, nuevoDocumento, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Documento agregado exitosamente');
      setShowAgregarDocumento(false);
      setNuevoDocumento({
        tipo: 'certificado',
        nombre: '',
        url: '',
        fecha_emision: '',
        descripcion: ''
      });
      cargarPerfilCompleto();
    } catch (error) {
      console.error('Error agregando documento:', error);
      toast.error('Error al agregar documento');
    }
  };

  const handleEliminarDocumento = async (documentoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este documento?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/me/documentos/${documentoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Documento eliminado');
      cargarPerfilCompleto();
    } catch (error) {
      console.error('Error eliminando documento:', error);
      toast.error('Error al eliminar documento');
    }
  };

  const handleAgregarCapacitacion = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/me/capacitaciones`, nuevaCapacitacion, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Capacitación agregada exitosamente');
      setShowAgregarCapacitacion(false);
      setNuevaCapacitacion({
        tipo: 'externa',
        nombre: '',
        institucion: '',
        fecha_inicio: '',
        fecha_finalizacion: '',
        estado: 'completada',
        horas: '',
        certificado_url: '',
        notas: ''
      });
      cargarPerfilCompleto();
    } catch (error) {
      console.error('Error agregando capacitación:', error);
      toast.error('Error al agregar capacitación');
    }
  };

  const handleEliminarCapacitacion = async (capacitacionId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta capacitación?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/me/capacitaciones/${capacitacionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Capacitación eliminada');
      cargarPerfilCompleto();
    } catch (error) {
      console.error('Error eliminando capacitación:', error);
      toast.error('Error al eliminar capacitación');
    }
  };

  const handleAbrirEditarCapacitacion = (capacitacion) => {
    setEditandoCapacitacion({
      ...capacitacion,
      fecha_inicio: capacitacion.fecha_inicio ? capacitacion.fecha_inicio.split('T')[0] : '',
      fecha_finalizacion: capacitacion.fecha_finalizacion ? capacitacion.fecha_finalizacion.split('T')[0] : ''
    });
    setShowEditarCapacitacion(true);
  };

  const handleEditarCapacitacion = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/me/capacitaciones/${editandoCapacitacion.id}`, editandoCapacitacion, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Capacitación actualizada exitosamente');
      setShowEditarCapacitacion(false);
      setEditandoCapacitacion(null);
      cargarPerfilCompleto();
    } catch (error) {
      console.error('Error editando capacitación:', error);
      toast.error('Error al actualizar capacitación');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <i className="fas fa-spinner fa-spin text-4xl text-indigo-600"></i>
            <p className="mt-4 text-gray-600">Cargando perfil...</p>
          </div>
        </div>
      </div>
    );
  }

  const historial = perfilData?.historial_academico || {};
  const perfilProf = perfilData?.perfil_profesional || {};
  const esProfesor = user?.rol === 'profesor';
  const esGraduado = historial?.estado === 'graduado';
  const esAdmin = user?.rol === 'admin';

  // Función para subir foto de perfil
  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      setImagePreview(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      setImagePreview(null);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo es demasiado grande (máximo 10MB)');
      setImagePreview(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    setTimeout(async () => {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);

        await axios.post(`${API}/upload/profile-photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        toast.success('Foto de perfil actualizada exitosamente');
        setImagePreview(null);
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);
        
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error.response?.data?.detail || 'Error al procesar foto de perfil');
        setImagePreview(null);
      } finally {
        setUploading(false);
      }
    }, 500);
  };

  const ejecutarMigracion = async () => {
    if (!window.confirm('¿Ejecutar migración de capacitaciones históricas?\n\nEsto registrará automáticamente todas las clases para profesores completadas en los perfiles profesionales.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/migrar-capacitaciones-historicas`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Migración exitosa: ${response.data.capacitaciones_agregadas} capacitaciones agregadas a ${response.data.profesores_actualizados} profesores`);
      cargarPerfilCompleto();
    } catch (error) {
      console.error('Error en migración:', error);
      toast.error(error.response?.data?.detail || 'Error en la migración');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-3 md:p-6">
      <div className="max-w-6xl mx-auto space-y-4 md:space-y-6">
        
        {/* Botón Volver */}
        <Button 
          variant="ghost" 
          onClick={() => window.history.length > 2 ? window.history.back() : window.location.href = '/app/dashboard'}
          className="text-gray-600 hover:text-gray-800 bg-white shadow-sm"
          data-testid="back-button"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Volver
        </Button>
        
        {/* Header del Perfil - Responsive */}
        <div className="bg-white rounded-xl shadow-lg p-4 md:p-8 border-l-4 border-indigo-600">
          <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-6">
            <div className="relative flex-shrink-0">
              {imagePreview ? (
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-blue-300 shadow-md overflow-hidden">
                  <img
                    src={imagePreview}
                    alt="Vista previa"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-30 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-medium">Vista Previa</span>
                  </div>
                </div>
              ) : user.foto_perfil && getImageUrl(user.foto_perfil) ? (
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-gray-200 shadow-md overflow-hidden">
                  <img
                    src={getImageUrl(user.foto_perfil)}
                    alt="Foto de perfil"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-full flex items-center justify-center border-4 border-gray-200 shadow-md">
                  <span className="text-3xl md:text-4xl text-white font-bold">
                    {user.nombre[0]}{user.apellido[0]}
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-xl md:text-3xl font-bold text-gray-800">
                {perfilData?.nombre} {perfilData?.apellido}
              </h1>
              <div className="flex flex-wrap justify-center sm:justify-start items-center gap-2 md:gap-4 mt-2">
                <span className="px-2 md:px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs md:text-sm font-medium">
                  {user?.rol?.toUpperCase()}
                </span>
                {esGraduado && (
                  <span className="px-2 md:px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs md:text-sm font-medium">
                    🎓 GRADUADO {historial.fecha_graduacion ? new Date(historial.fecha_graduacion).getFullYear() : ''}
                  </span>
                )}
              </div>
              <p className="text-gray-600 mt-2 text-sm md:text-base truncate">
                <i className="fas fa-envelope mr-2"></i>
                {perfilData?.email}
              </p>
            </div>
          </div>
        </div>

        {/* Información Personal - Editable */}
        <Card className="shadow-lg border-0">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardTitle className="flex items-center text-indigo-800">
              <i className="fas fa-user-edit mr-3 text-2xl"></i>
              Información Personal
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleUpdateProfile} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    value={profileFormData.nombre}
                    onChange={(e) => handleProfileInputChange('nombre', e.target.value)}
                    required
                    data-testid="profile-nombre-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="apellido">Apellido *</Label>
                  <Input
                    id="apellido"
                    value={profileFormData.apellido}
                    onChange={(e) => handleProfileInputChange('apellido', e.target.value)}
                    required
                    data-testid="profile-apellido-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-gray-50"
                    data-testid="profile-email-input"
                  />
                  <p className="text-xs text-gray-500">
                    El email no se puede modificar
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="celular">Número de Celular</Label>
                  <Input
                    id="celular"
                    type="tel"
                    value={profileFormData.celular}
                    onChange={(e) => handleProfileInputChange('celular', e.target.value)}
                    placeholder="+598 99 123 456"
                    data-testid="profile-celular-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefono">Teléfono Fijo</Label>
                  <Input
                    id="telefono"
                    type="tel"
                    value={profileFormData.telefono}
                    onChange={(e) => handleProfileInputChange('telefono', e.target.value)}
                    placeholder="+598 2 123 4567"
                    data-testid="profile-telefono-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fecha_nacimiento">Fecha de Nacimiento</Label>
                  <Input
                    id="fecha_nacimiento"
                    type="date"
                    value={profileFormData.fecha_nacimiento ? profileFormData.fecha_nacimiento.substring(0, 10) : ''}
                    onChange={(e) => handleProfileInputChange('fecha_nacimiento', e.target.value)}
                    data-testid="profile-fecha-nacimiento-input"
                  />
                  {!profileFormData.fecha_nacimiento && (
                    <p className="text-xs text-amber-600">
                      <i className="fas fa-info-circle mr-1"></i>
                      Completá tu fecha de nacimiento para recibir una felicitación especial
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pais">País</Label>
                  <Input
                    id="pais"
                    value={profileFormData.pais}
                    onChange={(e) => handleProfileInputChange('pais', e.target.value)}
                    placeholder="Uruguay"
                    data-testid="profile-pais-input"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="ciudad">Ciudad</Label>
                  <Input
                    id="ciudad"
                    value={profileFormData.ciudad}
                    onChange={(e) => handleProfileInputChange('ciudad', e.target.value)}
                    placeholder="Montevideo"
                    data-testid="profile-ciudad-input"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-200">
                <Button
                  type="submit"
                  disabled={savingProfile}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                  data-testid="profile-update-button"
                >
                  {savingProfile ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      Guardar Cambios
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Tarjeta de Identificación */}
        <Card className="shadow-lg border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <i className="fas fa-id-card mr-3 text-2xl"></i>
                Tarjeta de Identificación
              </span>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowCardModal(true)}
                  className="bg-white/20 hover:bg-white/30 text-white text-sm"
                >
                  <i className="fas fa-expand mr-2"></i>
                  Ver Completa
                </Button>
                <Button
                  onClick={() => {
                    const printContent = document.getElementById('id-card-print');
                    if (printContent) {
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>Tarjeta de Identificación - ${user.nombre} ${user.apellido}</title>
                            <style>
                              * { margin: 0; padding: 0; box-sizing: border-box; }
                              body { 
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                                display: flex; justify-content: center; align-items: center;
                                min-height: 100vh; background: #f5f5f5; padding: 20px;
                              }
                              .id-card {
                                width: 340px; height: 480px;
                                background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #ec4899 100%);
                                border-radius: 20px; position: relative; overflow: hidden;
                                box-shadow: 0 20px 40px rgba(0,0,0,0.3);
                              }
                              .content { position: relative; z-index: 10; height: 100%; padding: 16px; display: flex; flex-direction: column; }
                              .header { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; background: rgba(255,255,255,0.15); border-radius: 12px; padding: 8px 12px; }
                              .logo-box { width: 40px; height: 40px; min-width: 40px; background: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
                              .logo-box img { width: 30px; height: 30px; object-fit: contain; }
                              .inst-name { color: white; font-weight: bold; font-size: 11px; letter-spacing: 0.5px; line-height: 1.3; }
                              .photo-section { display: flex; justify-content: center; margin: 10px 0; }
                              .photo-ring { width: 85px; height: 85px; background: rgba(255,255,255,0.25); border-radius: 50%; padding: 3px; }
                              .photo-circle { width: 100%; height: 100%; border-radius: 50%; border: 3px solid white; overflow: hidden; background: white; }
                              .photo-circle img { width: 100%; height: 100%; object-fit: cover; }
                              .info-section { background: white; border-radius: 14px; padding: 14px; margin: 8px 0; flex: 1; }
                              .user-name { font-size: 18px; font-weight: bold; color: #1f2937; text-align: center; margin-bottom: 6px; }
                              .user-role { font-size: 11px; color: white; background: linear-gradient(135deg, #4f46e5, #7c3aed); padding: 4px 12px; border-radius: 20px; display: inline-block; margin: 0 auto 10px; }
                              .info-row { display: flex; align-items: center; padding: 6px 0; border-bottom: 1px solid #e5e7eb; font-size: 12px; }
                              .info-row:last-child { border-bottom: none; }
                              .info-label { color: #6b7280; width: 70px; font-weight: 500; }
                              .info-value { color: #1f2937; font-weight: 600; flex: 1; font-size: 12px; }
                              .qr-section { display: flex; justify-content: center; padding: 6px 0; }
                              .qr-box { background: white; padding: 6px; border-radius: 10px; }
                            </style>
                          </head>
                          <body>
                            <div class="id-card">
                              <div class="content">
                                <div class="header">
                                  <div class="logo-box">
                                    <img src="https://customer-assets.emergentagent.com/job_stability-boost-7/artifacts/r0xf23t0_IbaLogonuevo.jpg" alt="IBA" />
                                  </div>
                                  <div class="inst-name">Instituto Bíblico<br/>Apostólico del Uruguay</div>
                                </div>
                                <div class="photo-section">
                                  <div class="photo-ring">
                                    <div class="photo-circle">
                                      ${user.foto_perfil ? `<img src="${getImageUrl(user.foto_perfil)}" alt="Foto" />` : `<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:28px;font-weight:bold;color:#4f46e5">${user.nombre[0]}${user.apellido[0]}</div>`}
                                    </div>
                                  </div>
                                </div>
                                <div class="info-section">
                                  <div class="user-name">${user.nombre} ${user.apellido}</div>
                                  <div style="text-align:center"><span class="user-role">${user.rol?.toUpperCase()}</span></div>
                                  <div class="info-row"><span class="info-label">Email:</span><span class="info-value">${user.email}</span></div>
                                  <div class="info-row"><span class="info-label">País:</span><span class="info-value">${user.pais || 'No especificado'}</span></div>
                                  <div class="info-row"><span class="info-label">Ciudad:</span><span class="info-value">${user.ciudad || 'No especificada'}</span></div>
                                  ${user.telefono ? `<div class="info-row"><span class="info-label">Teléfono:</span><span class="info-value">${user.telefono}</span></div>` : ''}
                                </div>
                                <div class="qr-section">
                                  <div class="qr-box">
                                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=70x70&data=${encodeURIComponent(JSON.stringify({ id: user.id, email: user.email, nombre: user.nombre + ' ' + user.apellido, rol: user.rol }))}" alt="QR Code" style="width:70px;height:70px;" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </body>
                        </html>
                      `);
                      printWindow.document.close();
                      setTimeout(() => {
                        printWindow.print();
                      }, 500);
                    }
                  }}
                  className="bg-white/20 hover:bg-white/30 text-white text-sm"
                >
                  <i className="fas fa-print mr-2"></i>
                  Imprimir
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Mini tarjeta preview */}
              <div id="id-card-print" className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 rounded-2xl p-4 text-white shadow-xl w-full md:w-80">
                <div className="text-center mb-3">
                  <div className="w-12 h-12 bg-white rounded-full mx-auto mb-2 flex items-center justify-center">
                    <img src="https://customer-assets.emergentagent.com/job_stability-boost-7/artifacts/r0xf23t0_IbaLogonuevo.jpg" alt="IBA" className="w-9 h-9 object-contain" />
                  </div>
                  <p className="text-xs font-bold tracking-wider">INSTITUTO BÍBLICO APOSTÓLICO DEL URUGUAY</p>
                </div>
                
                <div className="flex justify-center mb-3">
                  <div className="w-20 h-20 rounded-full border-3 border-white/50 overflow-hidden bg-white/20 p-1">
                    {user.foto_perfil ? (
                      <img src={getImageUrl(user.foto_perfil)} alt="Foto" className="w-full h-full object-cover rounded-full" />
                    ) : (
                      <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-indigo-600 text-2xl font-bold">
                        {user.nombre[0]}{user.apellido[0]}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-3 text-gray-800">
                  <p className="font-bold text-center text-sm">{user.nombre} {user.apellido}</p>
                  <div className="flex justify-center mt-1">
                    <Badge className="bg-indigo-600 text-white text-xs">{user.rol?.toUpperCase()}</Badge>
                  </div>
                  <div className="mt-2 text-xs space-y-1">
                    <p><span className="text-gray-500">Email:</span> {user.email}</p>
                    <p><span className="text-gray-500">País:</span> {user.pais || '-'}</p>
                  </div>
                </div>
                
                <div className="flex justify-center mt-3">
                  <div className="bg-white p-2 rounded-lg">
                    <QRCodeSVG 
                      value={JSON.stringify({ id: user.id, email: user.email, rol: user.rol })}
                      size={60}
                      level="M"
                    />
                  </div>
                </div>
              </div>
              
              {/* Información adicional */}
              <div className="flex-1 space-y-3">
                <div className="bg-indigo-50 rounded-lg p-4">
                  <h4 className="font-semibold text-indigo-800 mb-2">
                    <i className="fas fa-info-circle mr-2"></i>
                    Información de la Tarjeta
                  </h4>
                  <p className="text-sm text-gray-600">
                    Esta tarjeta de identificación es tu credencial oficial del Instituto Bíblico Apostólico. 
                    Puedes imprimirla o mostrar el código QR en eventos para verificar tu identidad.
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">
                    <i className="fas fa-qrcode mr-2"></i>
                    Código QR
                  </h4>
                  <p className="text-sm text-gray-600">
                    El código QR contiene tu información de identificación y puede ser escaneado 
                    para marcar asistencia en eventos y actividades del instituto.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Modal para ver tarjeta completa */}
        <Dialog open={showCardModal} onOpenChange={setShowCardModal}>
          <DialogContent data-testid="perfil-id-card-modal" className="max-w-md w-[calc(100vw-1.5rem)] p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
            <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-4 sm:p-6 text-white">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-white rounded-full mx-auto mb-3 flex items-center justify-center shadow-lg">
                  <img src="https://customer-assets.emergentagent.com/job_stability-boost-7/artifacts/r0xf23t0_IbaLogonuevo.jpg" alt="IBA" className="w-12 h-12 object-contain" />
                </div>
                <p className="text-sm font-bold tracking-wider">INSTITUTO BÍBLICO APOSTÓLICO</p>
                <p className="text-xs opacity-80">DEL URUGUAY</p>
              </div>
              
              <div className="flex justify-center mb-4">
                <div className="w-28 h-28 rounded-full border-4 border-white/50 overflow-hidden bg-white/20 p-1 shadow-xl">
                  {user.foto_perfil ? (
                    <img src={getImageUrl(user.foto_perfil)} alt="Foto" className="w-full h-full object-cover rounded-full" />
                  ) : (
                    <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-indigo-600 text-4xl font-bold">
                      {user.nombre[0]}{user.apellido[0]}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-4 text-gray-800 shadow-lg">
                <p className="font-bold text-center text-lg break-words">{user.nombre} {user.apellido}</p>
                <div className="flex justify-center mt-2 mb-3">
                  <Badge className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-1">
                    {user.rol?.toUpperCase()}
                  </Badge>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1 border-b border-gray-100 gap-0.5">
                    <span className="text-gray-500 shrink-0">Email:</span>
                    <span className="font-medium break-all sm:text-right">{user.email}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1 border-b border-gray-100 gap-0.5">
                    <span className="text-gray-500 shrink-0">País:</span>
                    <span className="font-medium break-words sm:text-right">{user.pais || 'No especificado'}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1 border-b border-gray-100 gap-0.5">
                    <span className="text-gray-500 shrink-0">Ciudad:</span>
                    <span className="font-medium break-words sm:text-right">{user.ciudad || 'No especificada'}</span>
                  </div>
                  {user.telefono && (
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1 gap-0.5">
                      <span className="text-gray-500 shrink-0">Teléfono:</span>
                      <span className="font-medium break-words sm:text-right">{user.telefono}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-center mt-4">
                <div className="bg-white p-3 rounded-xl shadow-lg">
                  <QRCodeSVG 
                    value={JSON.stringify({ id: user.id, email: user.email, nombre: `${user.nombre} ${user.apellido}`, rol: user.rol })}
                    size={100}
                    level="M"
                  />
                </div>
              </div>
              
              <p className="text-center text-xs mt-3 opacity-80">
                Escanea el código QR para verificar identidad
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Sección de Foto de Perfil y Contraseña */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Card para cambiar foto */}
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center text-indigo-800">
                <i className="fas fa-camera mr-3 text-2xl"></i>
                Foto de Perfil
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="text-center space-y-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <Button
                  onClick={() => fileInputRef.current.click()}
                  disabled={uploading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <i className={`fas ${uploading ? 'fa-spinner fa-spin' : 'fa-camera'} mr-2`}></i>
                  {uploading ? 'Procesando imagen...' : 
                   imagePreview ? 'Confirmar nueva foto' :
                   user.foto_perfil ? 'Cambiar Foto' : 'Subir Foto'}
                </Button>
                <p className="text-xs text-gray-500">
                  JPG, PNG o GIF. Máximo 10MB.<br />
                  Se optimizará automáticamente a 300x300px.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Card para cambiar contraseña */}
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center text-orange-800">
                <i className="fas fa-lock mr-3 text-2xl"></i>
                Seguridad de la Cuenta
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <PasswordChangeSection API={API} />
              
              {/* Separador */}
              <div className="border-t border-gray-200 pt-6">
                <BiometricAuth />
              </div>
              
              {/* Separador - Notificaciones Push */}
              <div className="border-t border-gray-200 pt-6">
                <PushNotificationSection API={API} user={user} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Historial Académico */}
        {(['estudiante', 'pastor', 'graduado'].includes(user?.rol) || esGraduado) && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
              <i className="fas fa-graduation-cap text-indigo-600"></i>
              Mi Formación Académica
            </h2>

            {esGraduado && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-700">
                    {historial.promedio_final || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600">Promedio Final</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-700">
                    {historial.semestres_cursados || 0}
                  </div>
                  <div className="text-sm text-gray-600">Semestres</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg">
                  <div className="text-sm font-medium text-purple-700">
                    {historial.fecha_ingreso ? new Date(historial.fecha_ingreso).toLocaleDateString() : 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600">Fecha Ingreso</div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-lg">
                  <div className="text-sm font-medium text-amber-700">
                    {historial.fecha_graduacion ? new Date(historial.fecha_graduacion).toLocaleDateString() : 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600">Graduación</div>
                </div>
              </div>
            )}

            {historial.titulo_obtenido && (
              <div className="bg-indigo-50 p-4 rounded-lg mb-6">
                <div className="flex items-center gap-3">
                  <i className="fas fa-award text-2xl text-indigo-600"></i>
                  <div>
                    <div className="font-bold text-gray-800">{historial.titulo_obtenido}</div>
                    <div className="text-sm text-gray-600">Título Obtenido</div>
                  </div>
                </div>
              </div>
            )}

            {/* Historial de Clases Cursadas */}
            {historialAcademico && historialAcademico.materias_cursadas.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-gray-700 flex items-center gap-2 mb-4">
                  <i className="fas fa-book text-indigo-600"></i>
                  Clases Cursadas ({historialAcademico.materias_cursadas.length})
                  {historialAcademico.estadisticas.promedio_general && (
                    <span className="ml-auto text-xl font-bold text-indigo-600">
                      Promedio: {historialAcademico.estadisticas.promedio_general}
                    </span>
                  )}
                </h3>

                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clase</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profesor</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Semestre</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Calificación</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {historialAcademico.materias_cursadas.map((materia, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{materia.nombre}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {materia.profesor || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {materia.fecha}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {materia.calificacion !== null ? (
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                                materia.calificacion >= 9 ? 'bg-green-100 text-green-800' :
                                materia.calificacion >= 7 ? 'bg-blue-100 text-blue-800' :
                                materia.calificacion >= 6 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {materia.calificacion}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">Sin calificar</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {materia.activa ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                                <i className="fas fa-circle text-xs mr-1"></i>
                                Activa
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                                Finalizada
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Resumen de estadísticas */}
                <div className="mt-4 grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-indigo-600">
                      {historialAcademico.estadisticas.total_clases}
                    </div>
                    <div className="text-xs text-gray-600">Clases Totales</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {historialAcademico.estadisticas.clases_con_calificacion}
                    </div>
                    <div className="text-xs text-gray-600">Con Calificación</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {historialAcademico.estadisticas.semestres_cursados}
                    </div>
                    <div className="text-xs text-gray-600">Semestres</div>
                  </div>
                </div>
              </div>
            )}

            {/* Documentos */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-700 flex items-center gap-2">
                  <i className="fas fa-folder-open text-indigo-600"></i>
                  Documentos ({historial.documentos?.length || 0})
                </h3>
                <button
                  onClick={() => setShowAgregarDocumento(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Agregar Documento
                </button>
              </div>

              {historial.documentos && historial.documentos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {historial.documentos.map((doc) => (
                    <div key={doc.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <i className="fas fa-file-pdf text-red-500"></i>
                            <span className="font-medium text-gray-800">{doc.nombre}</span>
                          </div>
                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                            {doc.tipo}
                          </span>
                          {doc.fecha_emision && (
                            <div className="text-xs text-gray-500 mt-2">
                              Emitido: {new Date(doc.fecha_emision).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                            title="Ver documento"
                          >
                            <i className="fas fa-external-link-alt"></i>
                          </a>
                          <button
                            onClick={() => handleEliminarDocumento(doc.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Eliminar"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-folder-open text-4xl mb-3 opacity-50"></i>
                  <p>No hay documentos agregados</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Botón de Migración - Solo Admin */}
        {esAdmin && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl shadow-lg p-6 border-l-4 border-amber-500">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <i className="fas fa-sync-alt text-amber-600"></i>
                  Migración de Capacitaciones Históricas
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Registrar automáticamente todas las clases para profesores completadas en los perfiles profesionales
                </p>
              </div>
              <button
                onClick={ejecutarMigracion}
                className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors"
              >
                <i className="fas fa-database mr-2"></i>
                Ejecutar Migración
              </button>
            </div>
          </div>
        )}

        {/* Perfil Profesional - Solo para Profesores */}
        {esProfesor && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
              <i className="fas fa-chalkboard-teacher text-indigo-600"></i>
              Desarrollo Profesional
            </h2>

            {/* Capacitaciones */}
            <div>
              {/* Estadísticas de Capacitaciones */}
              {perfilProf.capacitaciones && perfilProf.capacitaciones.length > 0 && (() => {
                const capacitacionesConCalificacion = perfilProf.capacitaciones.filter(c => c.calificacion !== null && c.calificacion !== undefined);
                const promedioCapacitaciones = capacitacionesConCalificacion.length > 0
                  ? (capacitacionesConCalificacion.reduce((sum, c) => sum + parseFloat(c.calificacion), 0) / capacitacionesConCalificacion.length).toFixed(1)
                  : null;
                const capacitacionesCompletadas = perfilProf.capacitaciones.filter(c => c.estado === 'completada').length;
                const capacitacionesEnCurso = perfilProf.capacitaciones.filter(c => c.estado === 'en_curso').length;

                return (
                  <div className="mb-6 p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl shadow-sm border border-indigo-100">
                    <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <i className="fas fa-chart-line text-indigo-600"></i>
                      Rendimiento en Capacitaciones
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                        <div className="text-3xl font-bold text-indigo-600">
                          {perfilProf.capacitaciones.length}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">Total Capacitaciones</div>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                        <div className="text-3xl font-bold text-green-600">
                          {capacitacionesCompletadas}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">Completadas</div>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                        <div className="text-3xl font-bold text-blue-600">
                          {capacitacionesEnCurso}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">En Curso</div>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                        {promedioCapacitaciones ? (
                          <>
                            <div className={`text-3xl font-bold ${
                              promedioCapacitaciones >= 9 ? 'text-green-600' :
                              promedioCapacitaciones >= 7 ? 'text-blue-600' :
                              promedioCapacitaciones >= 6 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {promedioCapacitaciones}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">Promedio General</div>
                          </>
                        ) : (
                          <>
                            <div className="text-2xl font-bold text-gray-400">
                              <i className="fas fa-minus"></i>
                            </div>
                            <div className="text-xs text-gray-600 mt-1">Sin Calificaciones</div>
                          </>
                        )}
                      </div>
                    </div>
                    {promedioCapacitaciones && (
                      <div className="mt-4 flex items-center justify-center gap-2">
                        {promedioCapacitaciones >= 9 && (
                          <span className="px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                            🏆 Excelente Desempeño
                          </span>
                        )}
                        {promedioCapacitaciones >= 7 && promedioCapacitaciones < 9 && (
                          <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                            ⭐ Buen Desempeño
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-700 flex items-center gap-2">
                  <i className="fas fa-book-reader text-indigo-600"></i>
                  Capacitaciones y Formación Continua ({perfilProf.capacitaciones?.length || 0})
                </h3>
                <button
                  onClick={() => setShowAgregarCapacitacion(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Agregar Capacitación
                </button>
              </div>

              {perfilProf.capacitaciones && perfilProf.capacitaciones.length > 0 ? (
                <div className="space-y-4">
                  {perfilProf.capacitaciones.map((cap) => (
                    <div key={cap.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                      cap.tipo === 'iba_capacitacion' || cap.automatica 
                        ? 'border-indigo-200 bg-gradient-to-r from-indigo-50 to-blue-50' 
                        : 'border-gray-200'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            {cap.estado === 'completada' && <span className="text-green-500">✅</span>}
                            {cap.estado === 'en_curso' && <span className="text-blue-500">🔵</span>}
                            <h4 className="font-bold text-gray-800">{cap.nombre}</h4>
                            {(cap.tipo === 'iba_capacitacion' || cap.automatica) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                📚 Capacitación IBA
                              </span>
                            )}
                          </div>
                          
                          {cap.institucion && (
                            <div className="text-sm text-gray-600 mb-1">
                              <i className="fas fa-university mr-2"></i>
                              {cap.institucion}
                            </div>
                          )}

                          {cap.instructor && (
                            <div className="text-sm text-gray-600 mb-1">
                              <i className="fas fa-chalkboard-teacher mr-2"></i>
                              Instructor: {cap.instructor}
                            </div>
                          )}
                          
                          <div className="flex flex-wrap gap-3 text-sm text-gray-600 mt-2">
                            {cap.fecha_inicio && (
                              <span>📅 {new Date(cap.fecha_inicio).toLocaleDateString()}</span>
                            )}
                            {cap.horas && (
                              <span>🕐 {cap.horas} horas</span>
                            )}
                            {cap.calificacion && (
                              <span>⭐ {cap.calificacion}</span>
                            )}
                          </div>
                          
                          {cap.certificado_url && (
                            <a
                              href={cap.certificado_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block mt-2 text-blue-600 hover:text-blue-800 text-sm"
                            >
                              <i className="fas fa-certificate mr-1"></i>
                              Ver Certificado
                            </a>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAbrirEditarCapacitacion(cap)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Editar"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          {/* Las capacitaciones automáticas de IBA no se pueden eliminar directamente */}
                          {!(cap.tipo === 'iba_capacitacion' || cap.automatica) && (
                            <button
                              onClick={() => handleEliminarCapacitacion(cap.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Eliminar"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-book-reader text-4xl mb-3 opacity-50"></i>
                  <p>No hay capacitaciones registradas</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal Agregar Documento */}
        {showAgregarDocumento && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Agregar Documento</h3>
              <form onSubmit={handleAgregarDocumento} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={nuevoDocumento.tipo}
                    onChange={(e) => setNuevoDocumento({...nuevoDocumento, tipo: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="certificado">Certificado</option>
                    <option value="diploma">Diploma</option>
                    <option value="transcript">Historial Académico</option>
                    <option value="reconocimiento">Reconocimiento</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                  <input
                    type="text"
                    value={nuevoDocumento.nombre}
                    onChange={(e) => setNuevoDocumento({...nuevoDocumento, nombre: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL (Google Drive o Link)</label>
                  <input
                    type="url"
                    value={nuevoDocumento.url}
                    onChange={(e) => setNuevoDocumento({...nuevoDocumento, url: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://drive.google.com/..."
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Emisión (opcional)</label>
                  <input
                    type="date"
                    value={nuevoDocumento.fecha_emision}
                    onChange={(e) => setNuevoDocumento({...nuevoDocumento, fecha_emision: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAgregarDocumento(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Agregar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Agregar Capacitación */}
        {showAgregarCapacitacion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Agregar Capacitación Externa</h3>
              <form onSubmit={handleAgregarCapacitacion} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={nuevaCapacitacion.nombre}
                    onChange={(e) => setNuevaCapacitacion({...nuevaCapacitacion, nombre: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Institución/Organización *</label>
                  <input
                    type="text"
                    value={nuevaCapacitacion.institucion}
                    onChange={(e) => setNuevaCapacitacion({...nuevaCapacitacion, institucion: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
                    <input
                      type="date"
                      value={nuevaCapacitacion.fecha_inicio}
                      onChange={(e) => setNuevaCapacitacion({...nuevaCapacitacion, fecha_inicio: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
                    <input
                      type="date"
                      value={nuevaCapacitacion.fecha_finalizacion}
                      onChange={(e) => setNuevaCapacitacion({...nuevaCapacitacion, fecha_finalizacion: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    value={nuevaCapacitacion.estado}
                    onChange={(e) => setNuevaCapacitacion({...nuevaCapacitacion, estado: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="completada">Completada</option>
                    <option value="en_curso">En Curso</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Horas Totales</label>
                  <input
                    type="number"
                    value={nuevaCapacitacion.horas}
                    onChange={(e) => setNuevaCapacitacion({...nuevaCapacitacion, horas: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    min="1"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link del Certificado (opcional)</label>
                  <input
                    type="url"
                    value={nuevaCapacitacion.certificado_url}
                    onChange={(e) => setNuevaCapacitacion({...nuevaCapacitacion, certificado_url: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notas Adicionales</label>
                  <textarea
                    value={nuevaCapacitacion.notas}
                    onChange={(e) => setNuevaCapacitacion({...nuevaCapacitacion, notas: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    rows="3"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAgregarCapacitacion(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Agregar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Editar Capacitación */}
        {showEditarCapacitacion && editandoCapacitacion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                <i className="fas fa-edit text-blue-600 mr-2"></i>
                Editar Capacitación
              </h3>
              <form onSubmit={handleEditarCapacitacion} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={editandoCapacitacion.nombre}
                    onChange={(e) => setEditandoCapacitacion({...editandoCapacitacion, nombre: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Institución/Organización *</label>
                  <input
                    type="text"
                    value={editandoCapacitacion.institucion}
                    onChange={(e) => setEditandoCapacitacion({...editandoCapacitacion, institucion: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
                    <input
                      type="date"
                      value={editandoCapacitacion.fecha_inicio || ''}
                      onChange={(e) => setEditandoCapacitacion({...editandoCapacitacion, fecha_inicio: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
                    <input
                      type="date"
                      value={editandoCapacitacion.fecha_finalizacion || ''}
                      onChange={(e) => setEditandoCapacitacion({...editandoCapacitacion, fecha_finalizacion: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    value={editandoCapacitacion.estado}
                    onChange={(e) => setEditandoCapacitacion({...editandoCapacitacion, estado: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="completada">Completada</option>
                    <option value="en_curso">En Curso</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Horas Totales</label>
                  <input
                    type="number"
                    value={editandoCapacitacion.horas || ''}
                    onChange={(e) => setEditandoCapacitacion({...editandoCapacitacion, horas: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Calificación</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editandoCapacitacion.calificacion || ''}
                    onChange={(e) => setEditandoCapacitacion({...editandoCapacitacion, calificacion: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    min="0"
                    max="10"
                    placeholder="Ej: 8.5"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link del Certificado (opcional)</label>
                  <input
                    type="url"
                    value={editandoCapacitacion.certificado_url || ''}
                    onChange={(e) => setEditandoCapacitacion({...editandoCapacitacion, certificado_url: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notas Adicionales</label>
                  <textarea
                    value={editandoCapacitacion.notas || ''}
                    onChange={(e) => setEditandoCapacitacion({...editandoCapacitacion, notas: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    rows="3"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditarCapacitacion(false);
                      setEditandoCapacitacion(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <i className="fas fa-save mr-2"></i>
                    Guardar Cambios
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerfilCompleto;
